/**
 * S60b / plan 2 §10.2 — live analysis run panel.
 * Loads GET /analysis/:id; updates from analysis:step / analysis:status
 * (or 5s poll when the socket is down). DOM text via text nodes only.
 */
import { el, clear } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { resolvedOf } from './shape.js';
import {
  applyStatusEvent,
  applyStepEvent,
  canControlRun,
  formatElapsed,
  groupSteps,
  isActiveStatus,
  isFinalStatus,
  shouldPoll,
  STATUS_ICON,
  stepDuration,
} from './run-state.js';
import { isSocketOpen, subscribeAnalysis } from '../../screens/chat/analysis-live.js';
import { confirmDialog } from '../confirm-dialog.js';
import { statusLabel, plainModels, confirmReason } from './run-labels.js';
import { createRunControls } from './run-controls.js';
import { renderPlan, planVersionOf } from './run-plan.js';

/**
 * @param {object} block
 * @param {object} [actions]
 * @returns {HTMLElement}
 */
export function renderRun(block, actions) {
  const data = resolvedOf(block);
  const runId = String((data && data.run_id) || (block && block.run_id) || '').trim();
  const get = (actions && typeof actions.apiGet === 'function')
    ? actions.apiGet : (path) => api(path);
  const post = (actions && typeof actions.apiPost === 'function')
    ? actions.apiPost : (path, body) => api(path, { method: 'POST', body });
  const liveSub = (actions && typeof actions.subscribeAnalysis === 'function')
    ? actions.subscribeAnalysis : subscribeAnalysis;
  const socketOpen = (actions && typeof actions.isSocketOpen === 'function')
    ? actions.isSocketOpen : isSocketOpen;
  const confirmFn = (actions && typeof actions.confirm === 'function')
    ? actions.confirm
    : (opts) => confirmDialog(opts);
  const nowFn = (actions && typeof actions.now === 'function') ? actions.now : () => Date.now();
  const who = {
    userId: actions && actions.userId != null ? String(actions.userId) : sessionUserId(),
    isAdmin: actions && typeof actions.isAdmin === 'boolean' ? actions.isAdmin : sessionIsAdmin(),
  };

  // One card for the whole run: status, the plan, then steps folded under
  // Details. Header, plan and steps are redrawn when run data changes; the
  // elapsed time ticks on its own; controls are built once and kept, so typing
  // in the instruction box and open Details/Tools survive updates.
  const head = el('div', { class: 'chat-run-head' });
  const planBox = el('div', { class: 'chat-run-plan-box' });
  const stepsBox = el('div', { class: 'chat-run-steps-box' });
  const details = el('details', { class: 'chat-run-details' }, el('summary', {}, 'Details'), stepsBox);
  const live = el('div', { class: 'chat-run-live', 'aria-live': 'polite' }, head, planBox, details);
  const controlsSlot = el('div', { class: 'chat-run-controls-slot' });
  const root = el('div', {
    class: 'chat-block chat-block-run',
    'data-block-type': 'run',
    'data-block-id': (block && block.id) || '',
    'data-run-id': runId,
    id: runId ? `analysis-run-${runId}` : '',
    'aria-label': 'Analysis progress',
  }, live, controlsSlot);

  let state = { run: null, steps: [], tierMode: null, models: null };
  let stopLive = null;
  let pollTimer = null;
  let tickTimer = null;
  let lastLiveAt = nowFn();
  let elapsedNode = null;
  let controls = null;
  const openTools = new Set();

  function elapsedText() {
    const run = state.run;
    if (!run) return '';
    return formatElapsed(
      run.started_at || run.queued_at || run.created_at,
      isFinalStatus(String(run.status || '')) ? (run.completed_at || run.updated_at) : null,
      nowFn()
    );
  }

  function tick() {
    if (elapsedNode) elapsedNode.textContent = elapsedText();
  }

  function paint() {
    clear(head);
    clear(planBox);
    clear(stepsBox);
    elapsedNode = null;
    if (!runId) {
      head.append(el('p', { class: 'chat-run-note' }, 'Missing run id.'));
      details.hidden = true;
      return;
    }
    const run = state.run;
    if (!run) {
      head.append(el('p', { class: 'chat-run-note' }, 'Starting the analysis…'));
      details.hidden = true;
      return;
    }
    const status = String(run.status || 'unknown');
    const modelsLine = plainModels(state.models != null ? state.models : run.models);
    elapsedNode = el('p', { class: 'chat-run-elapsed' }, elapsedText());
    head.append(
      el('p', { class: 'chat-run-status' }, statusLabel(status, run.status_detail)),
      elapsedNode,
    );
    const plan = renderPlan(run);
    if (plan) planBox.append(plan);
    else if (isActiveStatus(status)) {
      planBox.append(el('p', { class: 'chat-run-note' }, 'Working out what to look at…'));
    }

    if (modelsLine) stepsBox.append(el('p', { class: 'chat-run-models' }, modelsLine));
    const groups = groupSteps(state.steps);
    details.hidden = !groups.length && !modelsLine;
    if (groups.length) {
      const list = el('ul', { class: 'chat-run-steps', role: 'list' });
      for (const { step, tools } of groups) list.append(stageRow(step, tools));
      stepsBox.append(list);
    }
    syncControls();
  }

  function stageRow(step, tools) {
    const summary = step.summary ? String(step.summary)
      : (step.error ? String(step.error) : '');
    const dur = stepDuration(step);
    const row = el('li', {
      class: `chat-run-step is-${String(step.status || 'pending')}`,
      'data-step-id': step.id || '',
    },
    el('div', { class: 'chat-run-step-main' },
      el('span', { class: 'chat-run-icon', 'aria-hidden': 'true' }, STATUS_ICON[step.status] || '○'),
      el('div', { class: 'chat-run-step-body' },
        el('p', { class: 'chat-run-step-title' }, String(step.title || 'Step')),
        dur ? el('p', { class: 'chat-run-step-meta' }, dur) : null,
        summary ? el('p', { class: 'chat-run-step-summary' }, summary) : null)));
    if (tools && tools.length) {
      const key = String(step.id || step.title || '');
      const details = el('details', { class: 'chat-run-tools' });
      if (openTools.has(key)) details.open = true;
      details.addEventListener('toggle', () => {
        if (details.open) openTools.add(key);
        else openTools.delete(key);
      });
      details.append(
        el('summary', {}, `Tools (${tools.length})`),
        el('ul', { class: 'chat-run-tool-list', role: 'list' }, tools.map((tool) => el('li', {
          class: 'chat-run-tool',
        },
        el('span', { class: 'chat-run-tool-name' }, String(tool.title || tool.agent || 'tool')),
        tool.summary ? el('span', { class: 'chat-run-tool-summary' }, String(tool.summary)) : null)))
      );
      row.append(details);
    }
    return row;
  }

  function syncControls() {
    const run = state.run;
    if (!run || !canControlRun(run, who)) {
      if (controls) { controls.box.remove(); controls = null; }
      return;
    }
    if (!controls) {
      controls = createRunControls({
        runId,
        post,
        onCancel,
        onStarted(res) {
          if (res && res.run) state = { ...state, run: { ...state.run, ...res.run } };
          paint();
          syncTimers();
          load();
        },
      });
      controlsSlot.append(controls.box);
    }
    controls.setWaiting(String(run.status) === 'awaiting_confirmation', confirmReason(run));
  }

  async function onCancel() {
    const ok = await confirmFn({
      title: 'Cancel this analysis?',
      body: 'Bonnie will stop after the current step. Anything finished so far stays in this chat.',
      confirmLabel: 'Cancel analysis',
      cancelLabel: 'Keep running',
    });
    if (!ok) return;
    const res = await post(`/analysis/${encodeURIComponent(runId)}/cancel`);
    if (res && res.run) {
      state = { ...state, run: { ...state.run, ...res.run } };
      paint();
      syncTimers();
    }
  }

  function onStep(step) {
    lastLiveAt = nowFn();
    state = { ...state, steps: applyStepEvent(state.steps, step) };
    paint();
    // The plan is on the run row: fetch it once planning is done.
    if (step && step.stage === 'plan' && step.status === 'done' && !step.parent_id) load();
  }

  function onStatus(payload) {
    lastLiveAt = nowFn();
    const known = planVersionOf(state.run && state.run.stage_state);
    state = { ...state, run: applyStatusEvent(state.run, payload) };
    paint();
    syncTimers();
    // An instruction changed the plan: reload to show the new one.
    if (payload && Number(payload.plan_version) > known) load();
  }

  async function load() {
    if (!runId) { paint(); return; }
    const res = await get(`/analysis/${encodeURIComponent(runId)}`);
    if (!res || res.error) {
      clear(head);
      head.append(el('p', { class: 'chat-run-note is-error' },
        (res && res.error) ? String(res.error) : 'Could not load analysis.'));
      return;
    }
    state = {
      run: res.run || null,
      steps: Array.isArray(res.steps) ? res.steps : [],
      tierMode: res.tier_mode || (res.run && res.run.tier_mode) || null,
      models: res.models != null ? res.models : (res.run && res.run.models),
    };
    paint();
    syncTimers();
  }

  function syncTimers() {
    const status = state.run && state.run.status;
    if (tickTimer) { clearInterval(tickTimer); tickTimer = null; }
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    if (!isActiveStatus(status)) {
      if (stopLive) { stopLive(); stopLive = null; }
      return;
    }
    if (!stopLive) stopLive = liveSub(runId, { onStep, onStatus });
    tickTimer = setInterval(tick, 1000);
    pollTimer = setInterval(() => {
      if (shouldPoll(!!socketOpen(), state.run && state.run.status, nowFn() - lastLiveAt)) {
        lastLiveAt = nowFn();
        load();
      }
    }, 5000);
    // Node tests: don't keep the process alive on open panels.
    if (typeof tickTimer.unref === 'function') tickTimer.unref();
    if (typeof pollTimer.unref === 'function') pollTimer.unref();
  }

  root._runPanel = {
    getState: () => state,
    applyStep: onStep,
    applyStatus: onStatus,
    paint,
    load,
    syncTimers,
    setState(next) { state = next; paint(); syncTimers(); },
  };

  paint();
  load();
  return root;
}

function sessionUserId() {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('user') : '';
    const user = raw ? JSON.parse(raw) : null;
    return user && (user.id || user.user_id) ? String(user.id || user.user_id) : '';
  } catch { return ''; }
}

function sessionIsAdmin() {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('user') : '';
    const user = raw ? JSON.parse(raw) : null;
    return !!(user && user.role === 'admin');
  } catch { return false; }
}
